import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDeleteMenuProduct,
  useMenuCollections,
  useMenuProducts,
  useMenuSections,
  useUpdateMenuProduct,
} from "@/hooks/use-menu";
import { formatPrice } from "@/lib/format-price";
import { useOrgCurrency } from "@/hooks/use-org-settings";
import { useActiveOrganization } from "@/hooks/use-organizations";

const formatDate = (value: string) => {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

export default function MenuProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data, error, isLoading } = useMenuProducts(activeOrganization?.id);
  const { data: sectionsData } = useMenuSections(activeOrganization?.id);
  const { data: collectionsData } = useMenuCollections(activeOrganization?.id);
  const deleteProduct = useDeleteMenuProduct();
  const updateProduct = useUpdateMenuProduct();
  const currency = useOrgCurrency(activeOrganization?.id);

  const handleAvailabilityToggle = (productId: string, available: boolean) => {
    updateProduct.mutate(
      { id: productId, data: { available } },
      {
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  const handleDelete = (productId: string) => {
    deleteProduct.mutate(productId, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.products.notifications.deleted"));
      },
    });
  };

  const getSectionName = (sectionId: string | null) => {
    if (!(sectionId && sectionsData?.sections)) {
      return "-";
    }
    const section = sectionsData.sections.find((s) => s.id === sectionId);
    if (!section) {
      return "-";
    }
    if (!section.parentId) {
      return section.name;
    }
    const parentSection = sectionsData.sections.find((s) => s.id === section.parentId);
    return parentSection ? `${parentSection.name} / ${section.name}` : section.name;
  };

  const getCollectionName = (collectionId: string | null) => {
    if (!(collectionId && collectionsData?.collections)) {
      return "-";
    }
    const collection = collectionsData.collections.find((c) => c.id === collectionId);
    return collection?.name ?? "-";
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.products.title")}</CardTitle>
            <CardDescription>{t("menu.products.notifications.activeOrgRequired")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isSupermarket = activeOrganization.businessType === "supermarket";
  const copy = {
    description: isSupermarket
      ? t("menu.products.catalog.description", {
          defaultValue: "Gestiona los productos del catálogo de {{organization}}.",
          organization: activeOrganization.name,
        })
      : t("menu.products.description", {
          organization: activeOrganization.name,
        }),
    emptyDescription: isSupermarket
      ? t("menu.products.catalog.table.emptyDescription", {
          defaultValue: "Crea tu primer producto para el catálogo.",
        })
      : t("menu.products.table.emptyDescription"),
    sectionColumn: isSupermarket
      ? t("menu.products.catalog.table.section", {
          defaultValue: "Categoría / subcategoría",
        })
      : t("menu.products.table.section"),
    tableDescription: isSupermarket
      ? t("menu.products.catalog.table.description", {
          defaultValue: "Productos en este catálogo.",
        })
      : t("menu.products.table.description"),
    tableTitle: isSupermarket
      ? t("menu.products.catalog.table.title", {
          defaultValue: "Productos del catálogo",
        })
      : t("menu.products.table.title"),
  };

  let productsContent: ReactNode = (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <h2 className="font-medium text-base">{t("menu.products.table.emptyTitle")}</h2>
      <p className="mt-2 text-muted-foreground text-sm">{copy.emptyDescription}</p>
    </div>
  );

  if (isLoading) {
    productsContent = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    productsContent = <p className="text-destructive text-sm">{error.message}</p>;
  } else if (data?.products.length) {
    productsContent = (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("menu.products.table.name")}</TableHead>
            <TableHead>{t("menu.products.table.price")}</TableHead>
            <TableHead>{copy.sectionColumn}</TableHead>
            <TableHead>{t("menu.products.table.collection")}</TableHead>
            <TableHead>
              {t("menu.products.table.available", {
                defaultValue: "Disponible",
              })}
            </TableHead>
            <TableHead>{t("menu.products.table.updated")}</TableHead>
            <TableHead className="text-right">{t("menu.products.table.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.products.map((product) => (
            <TableRow key={product.id}>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{product.name}</p>
                  {product.description ? (
                    <p className="text-muted-foreground text-xs">{product.description}</p>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-sm">{formatPrice(product.price, currency)}</TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {getSectionName(product.sectionId)}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {getCollectionName(product.collectionId)}
              </TableCell>
              <TableCell>
                <Switch
                  checked={product.available}
                  disabled={updateProduct.isPending}
                  onCheckedChange={(next) => handleAvailabilityToggle(product.id, next)}
                />
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDate(product.updatedAt)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => navigate(`/menu/products/${product.id}/edit`)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconEdit className="size-4" />
                    <span className="sr-only">{t("menu.products.actions.edit")}</span>
                  </Button>
                  <Button
                    onClick={() => handleDelete(product.id)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconTrash className="size-4" />
                    <span className="sr-only">{t("menu.products.actions.delete")}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif font-semibold text-2xl tracking-tight">
            {t("menu.products.title")}
          </h1>
          <p className="text-muted-foreground text-sm">{copy.description}</p>
        </div>
        <Button onClick={() => navigate("/menu/products/new")} type="button">
          <IconPlus className="mr-2 size-4" />
          {t("menu.products.newButton")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{copy.tableTitle}</CardTitle>
          <CardDescription>{copy.tableDescription}</CardDescription>
        </CardHeader>
        <CardContent>{productsContent}</CardContent>
      </Card>
    </div>
  );
}
